import type { Station } from "../lib/radio";

/**
 * ChannelMark — renders an in-app brand tile for stations that don't
 * ship a cover image. Currently styled for DR P1-P8, but the fallback
 * (initial + accent gradient) works for any station so we can drop it
 * in anywhere the cover would normally go.
 */

type Props = {
  station: Station;
  size?: number;
  className?: string;
};

type Mark = { primary: string; secondary?: string };

function markFor(stationId: string, name: string): Mark {
  switch (stationId) {
    case "dr-p1": return { primary: "P1" };
    case "dr-p2": return { primary: "P2" };
    case "dr-p3": return { primary: "P3" };
    case "dr-p4kbh": return { primary: "P4", secondary: "KBH" };
    case "dr-p5": return { primary: "P5" };
    case "dr-p6beat": return { primary: "P6", secondary: "BEAT" };
    case "dr-p7mix": return { primary: "P7", secondary: "MIX" };
    case "dr-p8jazz": return { primary: "P8", secondary: "JAZZ" };
    default: {
      // first 1-2 visible letters of the station name
      const letters = name.replace(/[^A-Za-zÆØÅæøå0-9]/g, "");
      return { primary: letters.slice(0, 2).toUpperCase() || "·" };
    }
  }
}

export function ChannelMark({ station, size = 64, className }: Props) {
  const mark = markFor(station.id, station.name);
  const isDr = station.id.startsWith("dr-p") && !station.id.startsWith("dr-pod-");
  const fontSize = size * (mark.secondary ? 0.34 : 0.46);
  const subFontSize = size * 0.14;

  return (
    <div
      className={`channel-mark ${isDr ? "is-dr" : ""} ${className || ""}`}
      style={{
        // typed-as-CSS-vars so global.css can pick them up
        ["--mk-accent" as string]: station.accent,
        width: size,
        height: size,
        fontSize,
      }}
      aria-hidden
    >
      <span className="channel-mark-primary">{mark.primary}</span>
      {mark.secondary && (
        <span className="channel-mark-secondary" style={{ fontSize: subFontSize }}>
          {mark.secondary}
        </span>
      )}
    </div>
  );
}
